import { SuppliersStructureBaseModel } from '@abb-procure/data';
import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  computed,
  effect,
  input,
  output,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { QuicklinkDirective } from 'ngx-quicklink';
import { ContainsPipe } from '../../pipes/contains.pipe';
import { LoadingIndicator } from '../loading-indicator/loading-indicator.component';
import { NumberBadge } from '../number-badge/number-badge.component';

@Component({
  selector: 'procure-suppliers-tree',
  templateUrl: './suppliers-tree.component.html',
  styleUrl: './suppliers-tree.component.scss',
  imports: [
    LoadingIndicator,
    MatIcon,
    ContainsPipe,
    MatTooltip,
    RouterLink,
    QuicklinkDirective,
    NgTemplateOutlet,
    NumberBadge,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class SuppliersTree {
  readonly afterAllSelected = output<string>();

  readonly tree = input<SuppliersStructureBaseModel | null>(null);
  readonly label = input<string>();
  readonly currentId = input<string>();

  protected readonly expanded = signal<string[]>([]);
  protected readonly isLoading = signal(true);

  protected readonly companiesCount = computed(() => {
    const structure = this.stucture();

    if (!structure) {
      return 0;
    }

    return this.countChildren(structure, true);
  });

  protected readonly stucture = signal<SuppliersStructureBaseModel | null>(
    null,
  );

  constructor() {
    effect(() => {
      const structure = this.tree();

      this.stucture.set(structure);
      this.isLoading.set(false);
      this.expanded.set([]);

      if (structure?.isExpanded) {
        const key = this.expansionKey(structure);

        if (key) {
          this.expanded.set([key]);
        }

        this.updateExpanded(structure);
      }
    });
  }

  protected updateExpanded(item: SuppliersStructureBaseModel | null): void {
    const children = item?.children;

    if (!children?.length) {
      return;
    }

    children?.forEach((child) => {
      const key = this.expansionKey(child);

      if (child.isExpanded && key) {
        this.expanded.update((state) => [...state, key]);
      }

      this.updateExpanded(child);
    });
  }

  protected supplierDisplay(
    item: SuppliersStructureBaseModel | null,
    displayCountry: boolean = false,
  ): string {
    if (!item) {
      return '';
    }

    let display = item.name ?? '';

    if (displayCountry && item.country) {
      display += ` - ${item.country.name}`;
    } else if (item.city) {
      display += ` - ${item.city}`;
    }

    if (item.guid) {
      display += ` - ${item.guid}`;
    }

    return display;
  }

  protected toggleExpansion(item: SuppliersStructureBaseModel | null): void {
    const key = this.expansionKey(item);

    if (!item?.children?.length || !key) {
      return;
    }

    this.expanded.update((state) => {
      if (state.includes(key)) {
        return state.filter((element) => element !== key);
      }

      return [...state, key];
    });
  }

  protected expansionKey(
    item: SuppliersStructureBaseModel | null,
  ): string | null {
    if (!item) {
      return null;
    }

    const parts = [item.id, item.guid].filter(
      (value): value is string => !!value,
    );

    return parts.length ? parts.join('|') : null;
  }

  private countChildren(
    item: SuppliersStructureBaseModel,
    first?: boolean,
  ): number {
    const initialCount = item.isFolder || first ? 0 : 1;

    if (!item.children?.length) {
      return initialCount;
    }

    return item.children.reduce(
      (acc, child) => acc + this.countChildren(child),
      initialCount,
    );
  }
}
